import React, { useState, useEffect, useMemo } from 'react';
import { ConversationThread } from './claude/conversation_thread';
import { SuggestedTopic, CuriosityMode } from './claude/suggested_topic';

import './SuggestedTopicsTree.css'

interface SuggestedTopicsTreeProps {
  conversationThread: ConversationThread;
  onCreateNewConversation: (followUpQuestion?: string) => void;
}

type FilterOption = 'all' | CuriosityMode;

const SuggestedTopicsTree: React.FC<SuggestedTopicsTreeProps> = ({
  conversationThread,
  onCreateNewConversation
}) => {
  const [suggestedTopics, setSuggestedTopics] = useState<SuggestedTopic[]>(
    conversationThread.getSuggestedTopics()
  );
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('all');

  useEffect(() => {
    const updateTopics = () => {
      setSuggestedTopics(conversationThread.getSuggestedTopics());
    };

    conversationThread.registerOnSuggestedTopicsChanged(updateTopics);

    return () => {
      conversationThread.unregisterOnSuggestedTopicsChanged();
    };
  }, [conversationThread]);

  // Filter topics based on selected curiosity mode
  const filteredTopics = useMemo(() => {
    if (selectedFilter === 'all') {
      return suggestedTopics;
    }
    return suggestedTopics.filter(topic => topic.curiosityMode === selectedFilter);
  }, [suggestedTopics, selectedFilter]);

  if (suggestedTopics.length === 0) {
    return null;
  }

  const handleTopicClick = (topic: SuggestedTopic) => {
    // Create new conversation and send the follow-up question
    onCreateNewConversation(topic.followUpQuestion);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFilter(event.target.value as FilterOption);
  };

  return (
    <div className="suggested-topics-panel">
      <div className="suggested-topics-header">
        <h3>Suggested Explorations</h3>
        <div className="curiosity-filter">
          <label htmlFor="curiosity-mode-select" className="filter-label">
            Learning Mode:
          </label>
          <select
            id="curiosity-mode-select"
            value={selectedFilter}
            onChange={handleFilterChange}
            className="curiosity-mode-select"
          >
            <option value="all">All</option>
            <option value="focused">Focused Learning</option>
            <option value="wandering">Curious Wandering</option>
          </select>
        </div>
      </div>
      <div className="suggested-topics-list">
        {/* <button
          onClick={() => onCreateNewConversation()}
          className="new-conversation-button"
          title="Start new conversation"
        >
          +
        </button> */}
        {filteredTopics.map((topic, index) => (
          <div key={index} className="suggested-topic-item" onClick={() => handleTopicClick(topic)}>
            <div className="topic-question">{topic.followUpQuestion}</div>
            <div className="topic-description">{topic.relevanceDescription}</div>
            <div className="topic-tags">
              <span className={`topic-tag curiosity-${topic.curiosityMode}`}>
                {topic.curiosityMode}
              </span>
              <span className={`topic-tag category-${topic.category}`}>
                {topic.category}
              </span>
            </div>
          </div>
        ))}
        {filteredTopics.length === 0 && selectedFilter !== 'all' && (
          <div className="no-topics-message">
            No {selectedFilter} topics available. Try selecting a different filter.
          </div>
        )}
      </div>
    </div>
  );
};

export default SuggestedTopicsTree;